import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type { SelectedFilterField } from '@cdw-ae/filter-bar-interfaces';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import * as FilterActions from '../../+state/filter/filter.actions';
import { CardSize } from '../../layouts/internal/components/dashboard/dashboard.model';
import {
  ChartActionTrigger,
  ChartCustomColor,
  ChartData,
  ChartSelectEvent,
  TooltipTextFunction,
} from './models/chart.model';

@Directive()
export abstract class BaseChartAbstract {
  public readonly CardSize = CardSize;
  @Input() dataSource!: number | object | object[] | null;
  // Common chart data and basic configuration
  @Input() chartData: ChartData[] = [];
  @Input() cardSize!: CardSize;
  @Input() colorScheme = 'cool';
  @Input() customColors: ChartCustomColor[] = [];
  @Input() chartType!: string;

  // Display options
  @Input() gradient = false;
  @Input() showLegend = false;
  @Input() legendTitle = 'Legend';
  @Input() legendPosition = 'right';
  @Input() animations = true;

  // Axis configuration
  @Input() showXAxis = true;
  @Input() showYAxis = true;
  @Input() showXAxisLabel = true;
  @Input() showYAxisLabel = true;

  // Label configuration
  @Input() labels = true;
  @Input() labelFormatting?: (value: string | number) => string;
  @Input() trimLabels = true;
  @Input() maxLabelLength = 10;
  @Input() leftAlign = false;

  // Tooltip configuration
  @Input() tooltipDisabled = false;
  @Input() tooltipText?: TooltipTextFunction;

  // Interactive elements
  @Input() activeEntries: ChartSelectEvent[] = [];

  // Pie/Doughnut specific (will be ignored by other chart types)
  @Input() explodeSlices = false;
  @Input() doughnut = false;
  @Input() arcWidth = 0.25;

  // Bar chart specific (will be ignored by other chart types)
  @Input() showGridLines = true;
  @Input() showDataLabel = false;
  @Input() yScaleMin?: number;
  @Input() yScaleMax?: number;

  // Common outputs
  @Output() select = new EventEmitter<ChartSelectEvent>();
  @Output() activate = new EventEmitter<ChartSelectEvent>();
  @Output() deactivate = new EventEmitter<ChartSelectEvent>();

  @Input() actionTrigger?: Subject<ChartActionTrigger>;
  @Input() currentFilters?: SelectedFilterField[];

  constructor(
    public store: Store,
    private elementRef: ElementRef
  ) {}
  // Common event handlers
  onSelect(event: { name?: string; value?: number }): void {
    if (this.chartType && (event?.name || event)) {
      this.store.dispatch(
        FilterActions.updateFilters({
          identifier: this.chartType,
          clickedValue: (typeof event === 'string' ? event : event.name) ?? '',
          filters: this.currentFilters || [],
        })
      );
    }
    this.select.emit(event);
  }
  onActivate(event: ChartSelectEvent): void {
    this.activate.emit(event);
  }

  onDeactivate(event: ChartSelectEvent): void {
    this.deactivate.emit(event);
  }

  // Utility methods that can be overridden by child classes
  protected calculateTotalValue(): number {
    return this.chartData.reduce((sum, item) => sum + (item.value || 0), 0);
  }

  protected shouldShowLegend(): boolean {
    return this.showLegend && this.cardSize === CardSize.LARGE;
  }

  // Abstract methods that must be implemented by child classes
  // These can be used for chart-specific initialization or custom logic
  protected abstract initializeChart(
    chartData: number | object | object[]
  ): ChartData[];
  protected abstract getCustomColors(): ChartCustomColor[];

  // Optional lifecycle methods that can be overridden
  protected onChartDataChange(): void {
    // Default implementation - can be overridden
  }

  protected onChartResize(): void {
    // Default implementation - can be overridden
  }

  protected executeAction(
    chartType: string,
    clickedValue?: string,
    targetFilterBar?: string
  ): void {
    this.store.dispatch(
      FilterActions.updateFilters({
        identifier: chartType,
        clickedValue,
        filterBarId: targetFilterBar,
      })
    );
  }

  ngOnInit(): void {
    if (this.actionTrigger) {
      this.actionTrigger.subscribe(
        ({ chartType, clickedValue, targetFilterBar }) =>
          this.executeAction(chartType, clickedValue, targetFilterBar)
      );
    }
    let normalizedData: Array<number | object> = [];

    if (typeof this.dataSource === 'number') {
      normalizedData = [{ value: this.dataSource }];
    } else if (Array.isArray(this.dataSource)) {
      normalizedData = this.dataSource;
    } else if (
      typeof this.dataSource === 'object' &&
      this.dataSource !== null
    ) {
      normalizedData = [this.dataSource];
    }

    if (normalizedData.length === 0) {
      this.customColors.map(({ name }) => ({ name, value: 0 }));
      this.chartData = [];
    } else {
      this.customColors = this.getCustomColors(); // 1. get colors first
      this.chartData = this.initializeChart(normalizedData); // 2. transform data
      this.onChartDataChange();
    }
  }

  ngAfterViewInit(): void {
    this.fixBarChartLabelRotation();
  }

  private fixBarChartLabelRotation(): void {
    const dataLabels =
      this.elementRef.nativeElement.querySelectorAll('.textDataLabel');
    dataLabels.forEach((element: HTMLElement) => {
      element.style.setProperty('transform', 'none', 'important');
      element.style.setProperty('text-anchor', 'middle', 'important');
    });
  }
}
